import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  TrendingUp, DollarSign, Clock, CheckCircle, XCircle,
  Loader2, ArrowRight, User, LogOut, Briefcase, PieChart,
  Calendar, CreditCard, Edit2, Save, X, Upload, FileCheck, ShieldCheck, Download
} from 'lucide-react';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import ReferralWallet from './dashboard/ReferralWallet';
import { DashboardSkeleton } from '../components/PageSkeletons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvestorDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [kycStatus, setKycStatus] = useState({});
  const [uploadingKyc, setUploadingKyc] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const investorData = localStorage.getItem('investor_data');
    if (!investorData) {
      navigate('/auth?type=investor');
      return;
    }
    fetchDashboard();
  }, [navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/investor/dashboard`);
      if (response.data.success) {
        setDashboard(response.data.dashboard);
        if (response.data.dashboard.profile) {
          setProfileData(response.data.dashboard.profile);
          setKycStatus(response.data.dashboard.profile.kyc || {});
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('investor_data');
        navigate('/auth?type=investor');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    localStorage.removeItem('investor_data');
    navigate('/');
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/investor/profile`, {
        phone: profileData.phone,
        bank_name: profileData.bank_name,
        account_number: profileData.account_number,
        account_name: profileData.account_name
      });
      toast.success('Profile updated successfully');
      setEditingProfile(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleKycUpload = async (docType, file) => {
    if (!file) return;
    setUploadingKyc(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/kyc/upload?document_type=${docType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        setKycStatus(prev => ({ ...prev, [docType]: res.data.file_url, status: 'pending' }));
        toast.success(`${docType === 'id_front' ? 'ID Front' : 'ID Back'} uploaded`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingKyc('');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      active: 'bg-green-100 text-green-700',
      completed: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      matured: 'bg-purple-100 text-purple-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-600';
  };

  const hideInvestmentHistory = async (investment) => {
    if (!window.confirm(`Hide ${investment.application_number} from your history? This does not delete the financial record.`)) return;
    try {
      await axios.post(`${API}/investments/${investment._id}/hide-history`);
      toast.success('Investment history hidden');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not hide investment history');
    }
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  const stats = dashboard?.stats || {};
  const investments = dashboard?.investments || [];
  const profile = dashboard?.profile || {};
  const referral = dashboard?.referral || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="investor-dashboard">
      {profile && profile.email && !profile.is_email_verified && (
        <EmailVerificationBanner email={profile.email} />
      )}
      {/* Header */}
      <div className="bg-gradient-to-r from-green-700 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Investor Dashboard</h1>
            <p className="text-green-200 text-sm">Welcome, {profile.full_name || 'Investor'}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/investment">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="new-investment-btn">
                <TrendingUp className="h-4 w-4 mr-2" /> New Investment
              </Button>
            </Link>
            <Link to="/account/security">
              <Button variant="ghost" className="text-white hover:bg-white/10" data-testid="investor-security-btn">
                <ShieldCheck className="h-4 w-4 mr-2" /> Security
              </Button>
            </Link>
            <Button variant="ghost" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="investor-logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Invested</p>
                  <p className="text-lg font-bold" data-testid="stat-total-invested">
                    ₦{(stats.total_invested || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Expected Returns</p>
                  <p className="text-lg font-bold text-green-600" data-testid="stat-total-returns">
                    ₦{(stats.total_returns || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <PieChart className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Portfolio Value</p>
                  <p className="text-lg font-bold" data-testid="stat-portfolio">
                    ₦{(stats.total_portfolio_value || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Briefcase className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Active Investments</p>
                  <p className="text-lg font-bold" data-testid="stat-active">
                    {stats.active_investments || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          {['overview', 'investments', 'profile'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-green-600 text-green-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="h-5 w-5" /> Recent Investments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {investments.length === 0 ? (
                  <div className="text-center py-8">
                    <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">No investments yet</p>
                    <Link to="/investment">
                      <Button className="bg-green-600 hover:bg-green-700" data-testid="start-investing-btn">
                        Start Investing <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {investments.slice(0, 5).map((inv) => (
                      <div key={inv._id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div>
                          <p className="font-medium">{inv.plan_name || inv.plan_id}</p>
                          <p className="text-sm text-gray-500">
                            {inv.application_number} | {new Date(inv.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">₦{(inv.amount || 0).toLocaleString()}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(inv.status)}`}>
                            {inv.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Investments Tab */}
        {activeTab === 'investments' && (
          <div className="space-y-4">
            {investments.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">No investments yet. Start building your portfolio!</p>
                  <Link to="/investment">
                    <Button className="bg-green-600 hover:bg-green-700">
                      Browse Investment Plans <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              investments.map((inv) => (
                <Card key={inv._id}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{inv.plan_name || inv.plan_id}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(inv.status)}`}>
                            {inv.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">Ref: {inv.application_number}</p>
                        <div className="flex gap-4 mt-2 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {new Date(inv.created_at).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {inv.duration_months} months
                          </span>
                        </div>
                      </div>
                      <div className="text-right space-y-1">
                        <p className="text-lg font-bold">₦{(inv.amount || 0).toLocaleString()}</p>
                        <p className="text-sm text-green-600 font-medium">
                          +₦{(inv.expected_return || 0).toLocaleString()} ({inv.return_percentage}%)
                        </p>
                        <p className="text-xs text-gray-500">
                          Payout: ₦{(inv.total_payout || 0).toLocaleString()}
                        </p>
                        {inv.status === 'matured' && <a href={`${API}/investments/${inv._id}/maturity-statement`}><Button variant="outline" size="sm" data-testid={`download-maturity-statement-${inv._id}`}><Download className="h-4 w-4 mr-1" />Statement</Button></a>}
                        {['rejected', 'completed', 'matured'].includes(inv.status) && <Button variant="ghost" size="sm" className="text-red-600" onClick={() => hideInvestmentHistory(inv)} data-testid={`hide-investment-history-${inv._id}`}>Hide history</Button>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5" /> Investor Profile
              </CardTitle>
              {!editingProfile ? (
                <Button variant="outline" size="sm" onClick={() => setEditingProfile(true)} data-testid="edit-profile-btn">
                  <Edit2 className="h-4 w-4 mr-1" /> Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingProfile(false)}>
                    <X className="h-4 w-4 mr-1" /> Cancel
                  </Button>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={handleSaveProfile} disabled={savingProfile} data-testid="save-profile-btn">
                    <Save className="h-4 w-4 mr-1" /> {savingProfile ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <ReferralWallet referral={referral} onChanged={fetchDashboard} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Full Name</label>
                  <p className="font-medium">{profile.full_name || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="font-medium">{profile.email || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Phone</label>
                  {editingProfile ? (
                    <Input
                      value={profileData.phone || ''}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      placeholder="+234..."
                      data-testid="profile-phone"
                    />
                  ) : (
                    <p className="font-medium">{profile.phone || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Account Status</label>
                  <p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${profile.is_verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {profile.is_verified ? 'Verified' : 'Pending Verification'}
                    </span>
                  </p>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" /> Bank Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Bank Name</label>
                    {editingProfile ? (
                      <Input
                        value={profileData.bank_name || ''}
                        onChange={(e) => setProfileData({ ...profileData, bank_name: e.target.value })}
                        placeholder="Enter bank name"
                        data-testid="profile-bank-name"
                      />
                    ) : (
                      <p className="font-medium">{profile.bank_name || 'Not set'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Account Number</label>
                    {editingProfile ? (
                      <Input
                        value={profileData.account_number || ''}
                        onChange={(e) => setProfileData({ ...profileData, account_number: e.target.value })}
                        placeholder="Enter account number"
                        data-testid="profile-account-number"
                      />
                    ) : (
                      <p className="font-medium">{profile.account_number || 'Not set'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Account Name</label>
                    {editingProfile ? (
                      <Input
                        value={profileData.account_name || ''}
                        onChange={(e) => setProfileData({ ...profileData, account_name: e.target.value })}
                        placeholder="Enter account name"
                        data-testid="profile-account-name"
                      />
                    ) : (
                      <p className="font-medium">{profile.account_name || 'Not set'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* KYC Section */}
              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" /> KYC Verification
                  {kycStatus.status && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ml-2 ${
                      kycStatus.status === 'approved' ? 'bg-green-100 text-green-700' :
                      kycStatus.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {kycStatus.status}
                    </span>
                  )}
                </h3>
                {kycStatus.review_notes && (
                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded mb-3">
                    Admin Note: {kycStatus.review_notes}
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* ID Front */}
                  <div className="border rounded-lg p-4">
                    <label className="text-sm font-medium text-gray-500 block mb-2">ID Front</label>
                    {kycStatus.id_front ? (
                      <div>
                        <img src={`${BACKEND_URL}${kycStatus.id_front}`} alt="ID Front" className="w-full h-32 object-cover rounded mb-2" />
                        <p className="text-xs text-green-600 flex items-center gap-1"><FileCheck className="h-3 w-3" /> Uploaded</p>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                        {uploadingKyc === 'id_front' ? (
                          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                        ) : (
                          <Upload className="h-6 w-6 text-gray-400" />
                        )}
                        <span className="text-xs text-gray-500">Click to upload ID front</span>
                        <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleKycUpload('id_front', e.target.files[0])} data-testid="kyc-front-upload" />
                      </label>
                    )}
                  </div>
                  {/* ID Back */}
                  <div className="border rounded-lg p-4">
                    <label className="text-sm font-medium text-gray-500 block mb-2">ID Back</label>
                    {kycStatus.id_back ? (
                      <div>
                        <img src={`${BACKEND_URL}${kycStatus.id_back}`} alt="ID Back" className="w-full h-32 object-cover rounded mb-2" />
                        <p className="text-xs text-green-600 flex items-center gap-1"><FileCheck className="h-3 w-3" /> Uploaded</p>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                        {uploadingKyc === 'id_back' ? (
                          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                        ) : (
                          <Upload className="h-6 w-6 text-gray-400" />
                        )}
                        <span className="text-xs text-gray-500">Click to upload ID back</span>
                        <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleKycUpload('id_back', e.target.files[0])} data-testid="kyc-back-upload" />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InvestorDashboard;
